import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, Shield, ArrowRight, Building2, UserCircle, KeyRound, Mail } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { trackFullEvent } from "@/lib/tracking";

export default function Cadastro() {
  const [formData, setFormData] = useState({
    empresaNome: "",
    cnpj: "",
    adminNome: "",
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const handleCEPChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 14);
    setFormData(prev => ({ ...prev, cnpj: value }));
  };

  const formatCnpj = (value: string) => {
    const digits = value.replace(/\D/g, "");
    return digits
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.empresaNome || !formData.email || !formData.password || !formData.cnpj) {
      return toast.error("Preencha todos os campos obrigatórios");
    }

    if (formData.cnpj.length !== 14) {
      return toast.error("CNPJ deve ter 14 dígitos");
    }

    setLoading(true);

    try {
      // 1. Check for free plan
      const { data: planos } = await supabase
        .from("planos")
        .select("id")
        .eq("valor", 0)
        .eq("ativo", true)
        .maybeSingle();

      const planoId = planos?.id;

      // 2. Call edge function to create company and user
      // Since it's public signup, we need an edge function that doesn't require auth
      console.log("Calling public-signup with data:", { 
        nome: formData.empresaNome,
        cnpj: formData.cnpj,
        email: formData.email.trim().toLowerCase(),
        adminNome: formData.adminNome || formData.email,
        plano_id: planoId
      });

      // Use native fetch to avoid potential Auth header issues with supabase.functions.invoke
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/public-signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apikey": import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || ""
        },
        body: JSON.stringify({
          nome: formData.empresaNome,
          cnpj: formData.cnpj,
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          adminNome: formData.adminNome || formData.email,
          plano_id: planoId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Erro ao realizar cadastro");
      }

      const data = await response.json();

      if (data?.error) {
        throw new Error(data.error);
      }

      toast.success("Cadastro realizado com sucesso! Entrando...");
      
      // Tracking
      trackFullEvent("CompleteRegistration", {
        email: formData.email.trim().toLowerCase(),
        name: formData.adminNome || formData.email
      });
      trackFullEvent("StartTrial", {
        email: formData.email.trim().toLowerCase(),
        name: formData.adminNome || formData.email
      });
      
      // Auto-login after successful signup
      const loginRes = await signIn(formData.email.trim().toLowerCase(), formData.password);
      
      if (loginRes.error) {
        console.error("Auto-login error:", loginRes.error);
        navigate("/login");
      } else {
        navigate("/redirect");
      }
    } catch (err: any) {
      console.error("Signup exception details:", {
        message: err.message,
        stack: err.stack,
        cause: err.cause,
        name: err.name
      });
      toast.error(err.message || "Erro ao tentar realizar cadastro. Tente novamente.");
    } finally {

      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50/50">
      {/* Visual Side */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden bg-blue-600">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-500 via-transparent to-transparent opacity-40" />
        
        <div className="relative z-10 w-full flex flex-col justify-between p-8 md:p-16 text-white">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm shadow-lg">
              <Users className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight">RH Simples</span>
          </Link>

          <div>
            <h1 className="text-5xl font-extrabold tracking-tight leading-[1.1]">
              Comece agora seu <span className="text-blue-200">período gratuito de 7 dias.</span>
            </h1>
            <p className="mt-6 text-xl text-blue-100 font-medium max-w-lg">
              Sem cartão de crédito. Após o teste, apenas R$ 34,90/mês ou R$ 379,90/ano.
            </p>

            <div className="mt-12 space-y-6">
              {[
                "Cálculo automático de folha",
                "Gestão completa de funcionários",
                "Geração de holerites e documentos",
                "Relatórios de custos reais"
              ].map((text) => (
                <div key={text} className="flex items-center gap-3">
                  <div className="h-6 w-6 rounded-full bg-white/10 flex items-center justify-center">
                    <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                  <span className="font-semibold">{text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-blue-100/80">
            <Shield className="h-4 w-4" />
            <span>Dados protegidos por criptografia de nível bancário</span>
          </div>
        </div>
      </div>

      {/* Form Side */}
      <div className="flex w-full lg:w-[55%] items-center justify-center p-6 md:p-12 overflow-y-auto">
        <div className="w-full max-w-[480px] space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="lg:hidden flex flex-col items-center gap-4 mb-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 shadow-xl shadow-blue-200">
              <Users className="h-7 w-7 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight text-slate-900 text-center">RH Simples</span>
          </div>

          <div className="space-y-2 text-center lg:text-left">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">Criar minha conta</h2>
            <p className="text-slate-500">Junte-se a centenas de empresas que já simplificaram seu RH</p>
          </div>

          <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-2xl shadow-slate-200/50">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700 ml-1 flex items-center gap-2">
                    <Building2 className="h-3.5 w-3.5" /> Empresa *
                  </Label>
                  <Input
                    placeholder="Nome da Empresa"
                    className="h-11 px-4 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500/10 transition-all bg-slate-50/30"
                    value={formData.empresaNome}
                    onChange={(e) => setFormData({ ...formData, empresaNome: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700 ml-1">CNPJ *</Label>
                  <Input
                    placeholder="00.000.000/0000-00"
                    className="h-11 px-4 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500/10 transition-all bg-slate-50/30"
                    value={formatCnpj(formData.cnpj)}
                    onChange={handleCEPChange}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700 ml-1 flex items-center gap-2">
                  <UserCircle className="h-3.5 w-3.5" /> Seu Nome
                </Label>
                <Input
                  placeholder="Como devemos te chamar?"
                  className="h-11 px-4 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500/10 transition-all bg-slate-50/30"
                  value={formData.adminNome}
                  onChange={(e) => setFormData({ ...formData, adminNome: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700 ml-1 flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5" /> Email Corporativo *
                </Label>
                <Input
                  type="email"
                  placeholder="exemplo@empresa.com"
                  className="h-11 px-4 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500/10 transition-all bg-slate-50/30"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700 ml-1 flex items-center gap-2">
                  <KeyRound className="h-3.5 w-3.5" /> Senha de Acesso *
                </Label>
                <Input
                  type="password"
                  placeholder="Mínimo 6 caracteres"
                  className="h-11 px-4 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500/10 transition-all bg-slate-50/30"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                />
              </div>

              <div className="pt-2">
                <Button 
                  type="submit" 
                  className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-base shadow-lg shadow-blue-200/50 transition-all active:scale-[0.98] disabled:opacity-70" 
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      <span>Criando conta...</span>
                    </div>
                  ) : (
                    "Criar minha conta agora"
                  )}
                </Button>
              </div>
            </form>
          </div>

          <p className="text-center text-sm font-medium text-slate-500 pb-8">
            Já tem uma conta?{" "}
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-bold hover:underline underline-offset-4">
              Fazer login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
